import restRequestV3 from '@/utils/restRequestV3';

const BASE_URL = '/api/v3/store-users';

export default {
  /**
   * 获取当前用户在当前店铺的信息
   * @returns {Promise<Object>} 返回用户在店铺中的信息，包含：
   * - userId: 用户ID
   * - storeId: 店铺ID
   * - isAdmin: 是否是管理员
   * - isOwner: 是否是店主
   * - subStoreStatus: 加盟申请状态（仅当前店铺是总店时返回）【0待审核 1审核通过 2审核拒绝】
   */
  getCurrentStoreUser() {
    return restRequestV3.get(`${BASE_URL}/current`);
  },

  /**
   * 获取当前用户在所有店铺的信息（原始版本，不包含钱包信息）
   * @returns {Promise<Object>} 返回用户在所有店铺中的基本信息列表
   * 返回数据结构：数组，每个元素包含：
   * - storeUserId: 店铺用户ID
   * - userId: 用户ID
   * - storeId: 店铺ID
   * - storeName: 店铺名称
   * - isAdmin: 是否是管理员
   * - isOwner: 是否是店主
   * - isAgent: 是否是代理
   * - authorizationStatus: 授权状态
   * - createTime: 创建时间
   */
  getMyStores() {
    return restRequestV3.get(`${BASE_URL}/my-stores`);
  },

  // 切换关注状态
  toggleAttention() {
    return restRequestV3.post(`${BASE_URL}/attention`);
  },

  // 更新关注状态
  updateAttentionStatus(status) {
    return restRequestV3.post(`${BASE_URL}/attention/status`, { status });
  },

  // 申请私域访问授权
  submitPrivateStoreApply() {
    return restRequestV3.post(`${BASE_URL}/private-store/apply`);
  },

  /**
   * 获取当前商户所属客户列表
   * @param {Object} params 查询参数
   * @param {number} params.pageNum 页码
   * @param {number} params.pageSize 每页数量
   * @param {string} [params.search] 搜索关键词
   * @returns {Promise<Object>} 返回客户列表、分页信息和统计数据
   * 返回数据结构：
   * - customerList: 分页客户列表，包含用户基本信息和钱包信息
   *   - storeUserId: 店铺用户ID
   *   - user: 用户基本信息（姓名、电话、头像等）
   *   - wallet: 用户钱包信息（余额、佣金、累计充值等）
   * - walletStats: 钱包统计信息
   *   - totalBalance: 余额总额
   *   - totalRechargeBonusAmount: 充值赠送总额
   *   - totalCommissionAmount: 佣金总额
   * - commissionConfig: 店铺佣金配置
   *   - defaultCommissionRate: 默认分享奖励比例
   *   - commissionRate: 代理分成奖励比例
   */
  getCustomers(params) {
    return restRequestV3.get(`${BASE_URL}/customers`, params);
  },

  /**
   * 获取StoreUser详情
   * @param {number} storeUserId 店铺用户ID
   * @returns {Promise<Object>} 返回店铺用户详情
   */
  getStoreUserDetail(storeUserId) {
    return restRequestV3.get(`${BASE_URL}/${storeUserId}`);
  },

  /**
   * 更新StoreUser详情
   * @param {number} storeUserId 店铺用户ID
   * @param {Object} data 更新数据
   * @returns {Promise<boolean>} 返回更新结果
   */
  updateStoreUser(storeUserId, data) {
    return restRequestV3.put(`${BASE_URL}/${storeUserId}`, data);
  },

  /**
   * 获取当前商户代理列表
   * @param {Object} params 查询参数
   * @param {number} params.pageNum 页码
   * @param {number} params.pageSize 每页数量
   * @param {string} [params.search] 搜索关键词
   * @returns {Promise<Object>} 返回代理列表和分页信息
   */
  getAgents(params) {
    return restRequestV3.get(`${BASE_URL}/agents`, params);
  },

  /**
   * 获取当前商户员工列表（包含员工和管理员）
   * @param {Object} params 查询参数
   * @param {number} params.pageNum 页码
   * @param {number} params.pageSize 每页数量
   * @param {string} [params.search] 搜索关键词
   * @returns {Promise<Object>} 返回员工列表和分页信息
   */
  getEmployees(params) {
    return restRequestV3.get(`${BASE_URL}/employees`, params);
  },

  /**
   * 审核代理申请
   * @param {number} storeUserId 店铺用户ID
   * @param {Object} params 审核参数
   * @param {number} params.status 审核状态(2:通过,3:不通过)
   * @param {string} [params.remark] 审核备注
   * @returns {Promise<boolean>} 返回审核结果
   */
  auditAgent(storeUserId, params) {
    return restRequestV3.put(`${BASE_URL}/agents/${storeUserId}/audit`, null, { params });
  },

  /**
   * 提交代理申请
   * @param {Object} data 代理申请数据
   * @param {string} data.agentName 代理商姓名
   * @param {string} data.agentPhone 联系电话
   * @param {string} [data.agentEmail] 电子邮箱
   * @param {string} [data.agentAddress] 联系地址
   * @param {string} [data.agentRemark] 申请说明
   * @param {number} data.storeId 店铺ID
   * @returns {Promise} 返回申请结果
   */
  submitAgentApply(data) {
    return restRequestV3.post(`${BASE_URL}/agents/submit-agent-apply`, data);
  },

  /**
   * 调整代理佣金比例
   * @param {number} storeUserId 店铺用户ID
   * @param {Object} params 调整参数
   * @param {number} params.commissionRate 佣金比例(0-100的百分比数值)
   * @param {string} [params.remark] 调整备注
   * @returns {Promise<boolean>} 返回调整结果
   */
  adjustCommissionRate(storeUserId, params) {
    return restRequestV3.put(`${BASE_URL}/agents/${storeUserId}/commission-rate`, null, { params });
  },

  /**
   * 更新用户角色（管理员、员工、代理）
   * @param {number} storeUserId 店铺用户ID
   * @param {Object} params 角色参数
   * @param {number} [params.isAdmin] 是否管理员（0否 1是）
   * @param {number} [params.isEmployee] 是否员工（0否 1是）
   * @param {number} [params.isAgent] 是否代理（0否 1是）
   * @returns {Promise<boolean>} 返回更新结果
   */
  updateUserRoles(storeUserId, params) {
    return restRequestV3.put(`${BASE_URL}/${storeUserId}/roles`, null, { params });
  },

  /**
   * 获取当前用户的按钮权限列表
   * @param {Object} params 查询参数
   * @param {number} [params.userId] 用户ID（可选，不传则获取当前用户权限）
   * @returns {Promise<Array>} 返回权限码列表
   * 返回数据结构：字符串数组，每个元素为权限码（如：'customer:view', 'customer:add'）
   * @example
   * // 获取当前用户权限
   * const permissions = await storeUserApi.getPermissions();
   * console.log(permissions); // ['customer:view', 'customer:add', 'order:view']
   * 
   * // 获取指定用户权限
   * const permissions = await storeUserApi.getPermissions({ userId: 123 });
   */
  getPermissions(params = {}) {
    return restRequestV3.get(`${BASE_URL}/permissions`, params);
  },

  /**
   * 更新用户的按钮权限
   * @param {number} userId 用户ID
   * @param {Array} permissions 权限码列表
   * @returns {Promise<boolean>} 返回更新结果
   * @example
   * // 更新用户权限
   * await storeUserApi.updatePermissions(123, ['customer:view', 'customer:add', 'order:view']);
   */
  updatePermissions(userId, permissions) {
    return restRequestV3.put(`${BASE_URL}/permissions`, permissions, { params: { userId } });
  },

  /**
   * 检查用户权限
   * @param {string} permission 权限码
   * @param {number} [userId] 用户ID（可选，不传则检查当前用户）
   * @returns {Promise<boolean>} 返回是否有权限
   * @example
   * // 检查当前用户权限
   * const hasPermission = await storeUserApi.checkPermission('customer:view');
   * 
   * // 检查指定用户权限
   * const hasPermission = await storeUserApi.checkPermission('customer:view', 123);
   */
  checkPermission(permission, userId) {
    const params = { permission };
    if (userId) {
      params.userId = userId;
    }
    return restRequestV3.get(`${BASE_URL}/permissions/check`, params);
  },

  /**
   * 获取用户权限详情
   * @param {number} [userId] 用户ID（可选，不传则获取当前用户）
   * @returns {Promise<Object>} 返回权限详情
   * 返回数据结构：
   * - permissions: 权限码列表
   * - permissionConfigs: 按分类分组的权限配置
   * @example
   * const permissionDetails = await storeUserApi.getPermissionDetails(123);
   */
  getPermissionDetails(userId) {
    const params = userId ? { userId } : {};
    return restRequestV3.get(`${BASE_URL}/permissions/details`, params);
  },

  /**
   * 获取所有可用的权限配置
   * @returns {Promise<Array>} 返回权限配置列表
   * 返回数据结构：数组，每个元素包含：
   * - code: 权限码
   * - name: 权限名称
   * - description: 权限描述
   * - category: 权限分类
   * @example
   * const permissionConfigs = await storeUserApi.getPermissionConfigs();
   * console.log(permissionConfigs);
   * // [
   * //   { code: 'customer:view', name: '查看客户', description: '查看客户列表和详情', category: 'customer' },
   * //   { code: 'customer:add', name: '新增客户', description: '添加新客户', category: 'customer' }
   * // ]
   */
  getPermissionConfigs() {
    return restRequestV3.get(`${BASE_URL}/permission-configs`);
  }
};



// WEBPACK FOOTER //
// ./src/api/v3/storeUser.js